import { useCallback } from 'react';
import { css } from '@emotion/react';
import { Button, Icon, Link, Modal, Text, tokens } from '@hotjar/hotsauce';
import type { OwnedAccount } from '@hotjar/account';
import { useTranslation } from 'react-i18next';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { MODAL } from '~/trials/constants';
import { useTrack } from '~/trials/external-dependencies/domains/tracking/useTrack';
import { ChurnDeflectionModalStyles as S } from './ChurnDeflectionModal.styles';

interface ChurnDeflectionModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onCloseModal: () => void;
  onDowngrade: () => void;
  account: OwnedAccount | undefined;
}

const actions = [
  {
    key: 'annual-subscription',
    url: '/articles/360007436073-How-to-Pay-Yearly-for-a-Hotjar-Plan',
  },
  { key: 'compare-plans', url: 'https://www.hotjar.com/pricing/' },
];

// A temporary const value representing the highest trialed plans for each
// product, for all sites. All users can see the “Business” version of
// the post trial upsell; very few users sign up on a Plus trial, so for
// this first iteration, we do not need to calculate what the highest
// trialed plans are.
const tempHighestTrialedPlans = {
  analytics_product: 'business',
  feedback_product: 'business',
};

const entries = Object.entries(tempHighestTrialedPlans);

export const ChurnDeflectionModal = ({
  isOpen,
  onCloseModal,
  onDowngrade,
  account,
  isLoading,
}: ChurnDeflectionModalProps): JSX.Element => {
  const { t } = useTranslation('trials', { keyPrefix: 'post-trial.churn-deflection' });
  const helpHotjarUrl = useHelpHotjarUrl();

  const { click: trackClick, getGroupDataForAccount } = useTrack();

  const handleClickAction = useCallback(
    (key) => {
      trackClick(
        t(`${key}.action`),
        MODAL.name,
        account ? getGroupDataForAccount(account) : undefined,
      );
    },
    [account, getGroupDataForAccount, t, trackClick],
  );
  return (
    <Modal
      size="l"
      onClose={onCloseModal}
      appElementSelector="#root"
      contentLabel={t('content-label')}
      isOpen={isOpen}
    >
      <Modal.Header title={t('title')} subtitle={t('subtitle')} />
      <Modal.Body
        css={css`
          background-color: ${tokens.BRAND_COLORS.white};
          border: 1px solid ${tokens.color.ui_border_light};
        `}
      >
        <S.ProductsContainer>
          {entries.map(([product, plan]) => (
            <S.ProductFeaturesContainer key={product}>
              <Text as="h5" textType="header5">
                {t('products.title', { context: product })}
              </Text>
              <S.FeaturesList>
                {t<string, string[]>(`products.${product}.features.${plan}`, {
                  returnObjects: true,
                }).map?.((text) => (
                  <S.FeatureItem key={text}>
                    <Icon
                      icon="close"
                      color="text_warning"
                      css={css`
                        margin-top: 2px;
                      `}
                    />
                    <Text as="span" textType="body2" textColor="text_light">
                      {text}
                    </Text>
                  </S.FeatureItem>
                ))}
              </S.FeaturesList>
            </S.ProductFeaturesContainer>
          ))}
        </S.ProductsContainer>
        <S.CtaContainer>
          {actions.map(({ key, url }) => (
            <S.Stack direction="column" spacing="x2" key={key}>
              <Text as="p" textType="subtitle2">
                {t(`${key}.title`)}
              </Text>
              <Text as="p" textType="body2" textColor="text_light">
                {t(`${key}.description`)}
              </Text>
              <Link
                href={`${helpHotjarUrl}${url}`}
                onClick={() => handleClickAction(key)}
                kind="standalone"
                isExternal
              >
                {t(`${key}.action`)}
              </Link>
            </S.Stack>
          ))}
        </S.CtaContainer>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" data-testid="go-back" onClick={onCloseModal}>
          {t('go-back')}
        </Button>
        <Button
          isLoading={isLoading}
          kind="danger"
          onClick={onDowngrade}
          data-testid="confirm-downgrade"
        >
          {t('confirm-downgrade')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
